/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Dropdown, Menu, Upload } from 'antd';
import { UploadProps } from 'antd/lib/upload';
import { uniqBy } from 'lodash';
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation, Link } from 'react-router-dom';
import IconButton from '~components/_common/IconButton/IconButton';
import MobileViewContext from '~src/contexts/MobileViewContext';
import { useModalActions } from '~src/contexts/ModalsContext';
import UploadContext from '~src/contexts/UploadContext';
import { navigationItemsMobile } from '~src/data/navigationItems';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useProject } from '~src/hooks/api-hooks/useProject';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useIsPath } from '~src/hooks/useIsPath';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { useProjectId } from '~src/hooks/useProjectId';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { RIGHTS_LEVEL } from '~src/types/enums';
import { FileX } from '~src/types/upload';
import { appLog } from '~src/utils/log';
import { getParentsIds } from '~src/utils/tree';

interface StateBreadCrumb {
  name?: string;
  path: string;
  /** for first level items e.g. archive, recent */
  navItem?: Partial<typeof navigationItemsMobile[0]>;
  disabled?: boolean;
}

export const AboveNavMobile: React.FC = () => {
  const history = useHistory();
  const styles = useStyles(styleFn);
  const { pathname } = useLocation();
  const foldersQuery = useFolders({}, { enabled: false });
  const projectId = useProjectId();
  const projectQuery = useProject({ enabled: false });
  const intl = useIntl();
  const isSuperuser = useIsSuperuser();
  const currentFolder = useCurrentFolder();
  const createFolderModal = useModalActions('createFolderModal');

  const { setShowMobileView } = useContext(MobileViewContext);
  const { setUploadContext } = useContext(UploadContext);

  const containerRef = useRef<HTMLDivElement>(null);

  const [breadcrumbs, setBreadcrumbs] = useState<StateBreadCrumb[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isOnRoot = useIsPath('/archive', { endsWith: true });

  useEffect(() => {
    setIsDropdownOpen(false);
  }, [breadcrumbs]);

  useEffect(() => {
    // split url paths
    const paths = pathname.split('/').filter(Boolean);

    // SPECIAL case for public share page, display a disabled "Shared with me" item
    if (paths[0] === 'share') {
      const sharedNavItem = navigationItemsMobile.find(
        (navItem) => navItem.key === 'shared-with-me'
      ) as typeof navigationItemsMobile[0];

      return setBreadcrumbs([
        {
          name: intl.formatMessage({ id: sharedNavItem.nameTerm }),
          path: '',
          navItem: sharedNavItem,
          disabled: true,
        },
      ]);
    }

    // /projects/:projectId, should have Home item
    if (paths.length < 3) {
      setBreadcrumbs([
        {
          name: projectQuery.data?.name || intl.formatMessage({ id: 'home' }),
          path: '/',
          // navItem: homeNavItem,
        },
      ]);
      return;
    }

    // e.g. /projects/:projectId/archive
    if (paths[2]) {
      const crumbs: StateBreadCrumb[] = [];

      // get root navItem e.g. archive, recent etc.
      const navItem = navigationItemsMobile.find((v) => v.key === paths[2]);
      if (!navItem) return;

      crumbs.push({
        name: intl.formatMessage({ id: navItem.nameTerm }),
        path: navItem.path,
        navItem,
      });

      // paths[3] is folderId
      if (paths[3] && paths[2] === 'archive' && foldersQuery.data) {
        const parentIds = getParentsIds(foldersQuery.data, paths[3]);

        // create breadcrumbs for parents
        const parentCrumbs = parentIds
          .map((id) => {
            const dbFolder = foldersQuery.data?.find(
              (v) => String(v.id) === id
            );
            if (!dbFolder) return undefined;

            return {
              name: dbFolder.name || 'Unknown',
              path: `/archive/${dbFolder.id}`,
            };
          })
          .filter(Boolean) as StateBreadCrumb[];

        crumbs.push(...parentCrumbs);

        // last, add current folder
        const folder = foldersQuery.data?.find(
          (v) => String(v.id) === paths[3]
        );
        if (folder)
          crumbs.push({
            name: folder.name || 'Unknown',
            path: `/archive/${folder.id}`,
          });
      }

      setBreadcrumbs(crumbs);
      setTimeout(() => {
        containerRef.current?.scroll(containerRef.current.scrollWidth, 0);
      }, 300);
    }
  }, [pathname, foldersQuery.data, intl, projectQuery.data]);

  const breadcrumbsOverlay = (
    <Menu>
      {breadcrumbs.map((item) => (
        <Menu.Item
          key={item.path}
          onClick={() => {
            appLog('item path -> ', item.path);
          }}>
          <Link to={`/projects/${projectId}${item.path}`}>{item.name}</Link>
        </Menu.Item>
      ))}
    </Menu>
  );
  const handleUploadChange: UploadProps['onChange'] = (info) => {
    setUploadContext((prevFiles) => {
      const file = info.fileList.find((v) => v.uid === info.file.uid)
        ?.originFileObj as FileX | undefined;
      if (!file) return prevFiles;

      return uniqBy(
        [file, ...prevFiles],
        (v) =>
          `${v.name}@${
            v.webkitRelativePath === v.name ? '' : v.webkitRelativePath
          }`
      );
    });
  };

  const permissionLevel = currentFolder?.permission || 0;
  let uploadFolderAllowed = false;
  if (!isOnRoot) {
    uploadFolderAllowed = permissionLevel >= RIGHTS_LEVEL.WRITE;
  } else if (isSuperuser) {
    uploadFolderAllowed = true;
  }
  const uploadFileAllowed = permissionLevel >= RIGHTS_LEVEL.UPLOAD;
  // const uploadFolderAllowed = permissionLevel >= RIGHTS_LEVEL.WRITE;

  const dotsMenuOverlay = (
    <Menu>
      <Menu.Item>
        <Upload
          multiple
          beforeUpload={() => false}
          onChange={handleUploadChange}
          disabled={!uploadFileAllowed}>
          <div css={styles.item}>
            <IconButton
              iconStyle="far"
              icon="file-upload"
              customStyle={styles.icons}
              tooltip={intl.formatMessage({ id: 'upload-files' })}
              disabled={!uploadFileAllowed}
            />
            <span css={styles.text}>
              {intl.formatMessage({ id: 'upload-file' })}
            </span>
          </div>
        </Upload>
      </Menu.Item>
      <Menu.Item>
        <div
          css={styles.item}
          onClick={() =>
            uploadFolderAllowed &&
            createFolderModal.open({ parent: currentFolder })
          }>
          <IconButton
            iconStyle="far"
            icon="folder-plus"
            customStyle={styles.icons}
            disabled={!uploadFolderAllowed}
            tooltip={intl.formatMessage({ id: 'create-folder' })}
          />
          <span css={styles.text}>
            {intl.formatMessage({ id: 'create-folder' })}
          </span>
        </div>
      </Menu.Item>
      <Menu.Item>
        <div css={styles.item} onClick={() => setShowMobileView(false)}>
          <IconButton
            icon={['far', 'desktop-alt']}
            backgroundOff
            customStyle={styles.icons}
          />
          <span css={styles.text}>
            {intl.formatMessage({ id: 'desktop-view' })}
          </span>
        </div>
      </Menu.Item>
    </Menu>
  );
  return (
    <div css={styles.container}>
      <IconButton
        click={() => history.goBack()}
        icon={['fas', 'arrow-left']}
        backgroundOff
        customStyle={styles.backIcon}
      />

      {breadcrumbs.length > 1 ? (
        <Dropdown
          overlay={breadcrumbsOverlay}
          trigger={['click']}
          onVisibleChange={(visible) => setIsDropdownOpen(visible)}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
            onClick={(e) => e.preventDefault()}>
            <p className="ant-dropdown-link" css={styles.text}>
              {breadcrumbs[breadcrumbs.length - 1].name}
            </p>
            <IconButton
              icon={['fas', isDropdownOpen ? 'chevron-up' : 'chevron-down']}
              // customStyle={[{ color: 'red' }]}
              backgroundOff
            />
          </div>
        </Dropdown>
      ) : (
        <p css={styles.text}>
          {breadcrumbs.length > 0
            ? breadcrumbs[breadcrumbs.length - 1].name
            : ''}
        </p>
      )}

      <Dropdown
        trigger={['click']}
        placement={'bottomLeft'}
        arrow
        overlay={dotsMenuOverlay}>
        <a
          className="ant-dropdown-link"
          onClick={(e) => e.preventDefault()}
          css={styles.dotsAnchor}>
          <IconButton
            iconStyle="far"
            icon="ellipsis-v"
            customStyle={styles.dotIcon}
            color={'gray'}
          />
        </a>
      </Dropdown>
    </div>
  );
};

export const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    height: 60,
    backgroundColor: theme.colors.white,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow:
      'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
  },
  backIcon: {
    // color: theme.colors.darkGray,
    fontSize: '20px',
    margin: '0px 20px',
  },
  text: {
    color: 'black',
    marginTop: 'revert',
    fontSize: 20,
    margin: 0,
  },
  icons: {
    fontSize: 20,
    marginRight: 10,
  },
  dotsAnchor: {
    color: theme.colors.darkGray,
    paddingTop: 5,
    textDecoration: 'none',
    '&:hover': {
      color: theme.colors.darkGray,
    },
  },
  dotIcon: {
    marginRight: 20,
    fontSize: '30px',
  },
  item: {
    display: 'flex',
    flexDirection: 'row',
    padding: '2px 5px',
    cursor: 'pointer',

    '&:active': {
      backgroundColor: theme.colors.primaryLight,
    },
    '&:hover': {
      backgroundColor:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? `${theme.colors.secondaryLight} !important`
          : '',
    },

    '&:not(.ant-dropdown-menu-item-disabled)': {
      color: theme.colors.text.primary,
    },
  },
});
